// @ts-check

import { mapValidationMessage as validationMapper } from '@/helpers/map-validation-message'
import { i18n } from '@/i18n'
import * as Sentry from '@sentry/vue'
import { useVuelidate as vuelidateComposable } from '@vuelidate/core'
import * as _ from 'lodash-es'
import { computed, ref } from 'vue'

/**
 * @type {{ [identifier: string]: import('vue').Ref }}
 */
const externalResultMap = {}

/**
 * Internal helper for mapping validation error to correct message.
 * @param {{ $validator: string, $message: string, $params: any, $response: any }} input
 * @returns {string}
 */
const mapValidationMessage = ({ $validator, $message, $params, $response }) => {
  let message = `validation.${$validator}`

  if ($validator === '$externalResults') {
    message = $message
  }

  // Custom validations could return a ValidatorResponse object which can contain
  // necessary parameters
  const params = {
    ...$params,
    ...(typeof $response === 'object' ? $response : {})
  }

  if (i18n.global.te(message)) {
    return i18n.global.t(message, params)
  }

  if (i18n.global.te(`validation.${message}`)) {
    return i18n.global.t(`validation.${message}`, params)
  }

  return $message ?? message
}

/**
 * @param {Parameters<typeof vuelidateComposable>[0]} [validations]
 * @param {Parameters<typeof vuelidateComposable>[1]} [state]
 * @param {import('@vuelidate/core').GlobalConfig['$externalResults']} [externalResults]
 */
export const useVuelidate = (validations, state, externalResults) => {
  const $externalResults = externalResults ?? ref({})

  const v$ =
    validations && state
      ? vuelidateComposable(validations, state, { $externalResults })
      : vuelidateComposable({ $externalResults })

  // Simple flag indicating if the form is valid or not.
  const isFormValid = computed(() => !v$.value.$invalid)

  // All the field validation errors as a map.
  const validationErrors = computed(() =>
    v$.value.$errors.reduce((errorAcc, error) => {
      let errorMap = errorAcc

      if (!error) {
        return errorMap
      }

      // @ts-expect-error TODO: Add custom extended type
      const isExternalEach = !!error.$message?.$each
      const isClientEach = error.$validator === '$each'

      // Maps API or client collection validation errors.
      if (isExternalEach || isClientEach) {
        /** @type {{ [field: string]: string | string []}[]} */
        const errors = isExternalEach
          ? // @ts-expect-error TODO: Add custom extended type
            error.$message.$each
          : error.$response.$errors

        errors.forEach((each, index) => {
          Object.entries(each).forEach(([field, itemErrors]) => {
            const mappedField = `${error.$propertyPath}.${index}.${field}`

            if (!itemErrors.length) {
              return
            }

            const itemFieldError = isExternalEach
              ? { $validator: '$externalResults', $message: itemErrors }
              : itemErrors[0]

            errorMap = { [mappedField]: itemFieldError, ...errorMap }
          })
        })

        return errorMap
      }

      return { [error.$propertyPath]: error, ...errorMap }
    }, {})
  )

  /**
   * Translated error for components to display.
   * @type {import('vue').ComputedRef<Record<string, any>> }}
   */
  const errors = computed(() =>
    Object.entries(validationErrors.value).reduce(
      (errorAcc, [field, error]) => {
        const errorField = field.replace('form.', '')
        const message = mapValidationMessage(error)
        return _.set(
          _.set(errorAcc, errorField, [message]),
          `form.${errorField}`,
          [message]
        )
      },
      {}
    )
  )

  /**
   * Runs all Vuelidate validators synchronously and returns a flag indicating
   * if the form is valid or not.
   */
  const validateOnSubmit = () => {
    v$.value.$clearExternalResults()
    v$.value.$touch()
    return isFormValid.value
  }

  /**
   * Runs all Vuelidate validators asynchronously and returns a flag indicating
   * if the form is valid or not.
   */
  const asyncValidateOnSubmit = async () => {
    v$.value.$clearExternalResults()
    return v$.value.$validate()
  }

  /**
   * Update server validation messages from the given API error response.
   * Returns true if the form is valid.
   *
   * @param {any} exception
   * @param {{ [field: string]: string }} [fieldMap]
   * @param {{  silent?: boolean }} [options]
   */
  const setServerValidationMessages = (
    exception,
    fieldMap = {},
    options = {}
  ) => {
    v$.value.$clearExternalResults()

    // Only supports 422 for now.
    if (exception.response?.status !== 422) {
      Sentry.captureException(exception, {
        extra: {
          status: exception.response?.status
        }
      })
      return true
    }

    const { fieldErrors } = exception.response?.data?.error ?? {}

    // Safeguard if validation error returns no validation messages.
    if (!fieldErrors || !Object.keys(fieldErrors).length) {
      Sentry.captureException(exception, {
        extra: {
          body: exception.response?.data
        }
      })

      return true
    }

    $externalResults.value = Object.entries(fieldErrors).reduce(
      (errorAcc, [apiField, apiErrors]) => {
        // Replace API collection validation errors with client collection
        let field = apiField.replace(/\.(\d+)\./, '.$each.$1.')
        const errorMessage = validationMapper(apiErrors[0])

        if (field in fieldMap) {
          field = fieldMap[field]
        }

        // Add field to errors + form variant.
        const errorsWithField = _.set(errorAcc, field, errorMessage)
        return _.set(errorsWithField, `form.${field}`, errorMessage)
      },
      {}
    )

    v$.value.$touch()

    if (isFormValid.value && !options.silent) {
      Sentry.captureException(exception, {
        extra: {
          body: exception.response?.data
        }
      })
    }

    return isFormValid.value
  }

  return {
    v$,
    isFormValid,
    validationErrors,
    validateOnSubmit,
    asyncValidateOnSubmit,
    validate: asyncValidateOnSubmit,
    errors,
    setServerValidationMessages,
    getValidationMessage: (/** @type {string} */ field) =>
      _.get(errors.value, field),
    externalResults: $externalResults,
    serverValidationMessages: $externalResults
  }
}

/**
 *
 * @param {string} identifier
 * @param {Parameters<typeof useVuelidate>[0]} [validations]
 * @param {Parameters<typeof useVuelidate>[1]} [state]
 * @returns
 */
export const useSharedVuelidate = (identifier, validations, state) => {
  if (!externalResultMap[identifier]) {
    externalResultMap[identifier] = ref({})
  }

  externalResultMap[identifier].value = {}

  return useVuelidate(validations, state, externalResultMap[identifier])
}

export default useVuelidate
